import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { mergeExistingFields } from "helpers/mergeExistingFields";

import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import {
  GameItem,
  mergeGameItem,
  updateGame,
} from "../../gameCenter/games/slice";
import { ApiErrorPayload } from "types/ApiErrorPayload";
import {
  getChangedFields,
  normalizeOptionalText,
} from "helpers/getChangedFields";

export interface CashbackGamesItem {
  id: number;
  cashBackType: string;
  title: string;
  description: string;
}

interface CashbackGamesState {
  list: CashbackGamesItem[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  isItemFetched: boolean;
  selectedItems: number[];
  payload?: CashbackGamesItem;
  initialPayload?: CashbackGamesItem;
  activePageGames: number;
  totalPagesGames: number;
  totalItemsGames: number;
  isListGamesFetched: boolean;
  selectedItemsGames: number[];
  gamesList: GameItem[];
  isItemFetching: boolean;
  processingItemId?: CashbackGamesItem["id"];
  processingGameItemId?: GameItem["id"];
}
interface GetListPayload {
  limit: number;
  offset: number;
}

interface GetGamesListPayload {
  limit: number;
  offset: number;
  providerIds: number[];
  gameTitle?: string;
}

export const getGamesList = createAsyncThunk(
  "cashback-games/games/list",
  async (data: GetGamesListPayload) => {
    try {
      const result = await instance.post(
        `${apiRoutes.cashbacksGamesListGames}`,
        {
          ...data,
        },
      );
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const getCashbackGamesListList = createAsyncThunk(
  "cashback-games/list",
  async (data: GetListPayload) => {
    try {
      const result = await instance.get(
        `${apiRoutes.cashbacksGamesList}?limit=${data.limit}&offset=${data.offset}`,
      );
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const getCashbackGamesItem = createAsyncThunk(
  "cashback-games/item",
  async (data: { id: string }) => {
    try {
      const result = await instance.get(
        `${apiRoutes.cashbacksGamesItem}/${data.id}`,
      );
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const createCashbackGames = createAsyncThunk<
  number,
  CashbackGamesItem,
  { rejectValue: ApiErrorPayload }
>("cashback-games/create", async (data, { rejectWithValue }) => {
  try {
    const result = await instance.post(
      `${apiRoutes.cashbacksGamesCreate}`,
      data,
    );
    return result.data.id;
  } catch (error: any) {
    const apiError = error?.data?.error ?? error?.data;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});
export const updateCashbackGames = createAsyncThunk(
  "cashback-games/update",
  async (data: CashbackGamesItem, { getState }) => {
    try {
      const state = getState() as
        | CashbackGamesState
        | { "cashback-games": CashbackGamesState };
      const initialPayload =
        "cashback-games" in state
          ? state["cashback-games"].initialPayload
          : state.initialPayload;
      const changes = getChangedFields(
        initialPayload,
        data,
        ["title", "description", "cashBackType"],
        {
          title: normalizeOptionalText,
          description: normalizeOptionalText,
        },
      );
      await instance.put(`${apiRoutes.cashbacksGamesUpdate}/${data.id}`, {
        ...changes,
      });
      return true;
    } catch (error) {
      return false;
    }
  },
);
export const deleteCashbackGamesItem = createAsyncThunk(
  "cashback-games/delete",
  async (id: string | number) => {
    try {
      await instance.delete(`${apiRoutes.cashbacksGamesDelete}/${id}`);
      return true;
    } catch (error) {
      return false;
    }
  },
);

export const removeCashbackGames = createAsyncThunk(
  "cashback/games/remove",
  async ({
    cashbackId,
    gameIds,
  }: {
    cashbackId: number;
    gameIds: number[];
  }) => {
    try {
      const res = await instance.put(
        `${apiRoutes.cashbackRemoveGames}/${cashbackId}/remove-games`,
        { gameIds },
      );
      return res.data;
    } catch {
      return undefined;
    }
  },
);

const initialState: CashbackGamesState = {
  list: [],
  isListFetched: false,
  isItemFetched: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
  isListGamesFetched: false,
  activePageGames: 1,
  totalItemsGames: 0,
  totalPagesGames: 0,
  selectedItemsGames: [],
  gamesList: [],
  isItemFetching: false,
};

export const cashbackGamesSlice = createSlice({
  name: "cashback-games",
  initialState,
  reducers: {
    handlePaginate: (
      state: CashbackGamesState,
      action: PayloadAction<number>,
    ) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleClear: (state: CashbackGamesState) => {
      state.activePage = 1;
      state.isListFetched = false;
      state.selectedItems = [];
      state.list = [];
      state.totalItems = 1;
      state.totalPages = 1;
    },
    handleClearFields: (state: CashbackGamesState) => {
      state.payload = undefined;
      state.initialPayload = undefined;
      state.isItemFetched = false;
    },
    handleUpdateFields: (
      state: CashbackGamesState,
      action: PayloadAction<CashbackGamesItem>,
    ) => {
      state.payload = { ...state.payload, ...action.payload };
    },
    handleSelectItem: (
      state: CashbackGamesState,
      action: PayloadAction<number>,
    ) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems = [...state.selectedItems, action.payload];
      }
    },
    handleSelectAll: (state: CashbackGamesState) => {
      if (state.selectedItems.length === state.list.length) {
        state.selectedItems = [];
      } else {
        state.selectedItems = state.list.map((el) => el.id ?? 0);
      }
    },
    handlePaginateGames: (
      state: CashbackGamesState,
      action: PayloadAction<number>,
    ) => {
      state.activePageGames = action.payload;
      state.isListGamesFetched = false;
      state.selectedItemsGames = [];
    },
    handleSelectItemGames: (
      state: CashbackGamesState,
      action: PayloadAction<number>,
    ) => {
      if (state.selectedItemsGames.includes(action.payload)) {
        state.selectedItemsGames = state.selectedItemsGames.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItemsGames = [
          ...state.selectedItemsGames,
          action.payload,
        ];
      }
    },
    handleSelectAllGames: (state: CashbackGamesState) => {
      if (state.selectedItemsGames.length === state.gamesList.length) {
        state.selectedItemsGames = [];
      } else {
        state.selectedItemsGames = state.gamesList.map((el) => el.id ?? 0);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCashbackGamesListList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(getCashbackGamesItem.fulfilled, (state, action) => {
      state.isItemFetched = true;
      state.payload = {
        ...action.payload,
        wagerTitle: action.payload.wagerTitle
          ? parseInt(action.payload.wagerTitle, 10)
          : undefined,
        wagerGameListId: action.payload.wagerTitle
          ? parseInt(action.payload.wagerTitle, 10)
          : undefined,
      };
      state.initialPayload = { ...state.payload } as CashbackGamesItem;
    });
    builder.addCase(createCashbackGames.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(createCashbackGames.fulfilled, (state) => {
      state.isListFetched = false;
      state.isItemFetching = false;
    });
    builder.addCase(createCashbackGames.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(updateCashbackGames.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updateCashbackGames.fulfilled, (state, action) => {
      state.list = state.list.map((item) =>
        item.id === action.meta.arg.id
          ? mergeExistingFields(item, action.meta.arg)
          : item,
      );
      state.isItemFetching = false;
      state.processingItemId = undefined;
      state.initialPayload = state.payload ? { ...state.payload } : undefined;
    });
    builder.addCase(updateCashbackGames.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(getGamesList.pending, (state) => {
      state.isListGamesFetched = false;
    });
    builder.addCase(getGamesList.fulfilled, (state, action) => {
      state.isListGamesFetched = true;
      state.gamesList = action.payload?.items ?? [];
      state.totalItemsGames = action.payload?.totalItems ?? 1;
      state.totalPagesGames = action.payload?.totalPages ?? 1;
    });
    builder.addCase(updateGame.pending, (state, action) => {
      state.processingGameItemId = action.meta.arg.id;
    });
    builder.addCase(updateGame.fulfilled, (state, action) => {
      state.gamesList = state.gamesList.map((game) =>
        game.id === action.meta.arg.id
          ? mergeGameItem(game, action.payload)
          : game,
      );
      state.processingGameItemId = undefined;
    });
    builder.addCase(updateGame.rejected, (state) => {
      state.processingGameItemId = undefined;
    });
    builder.addCase(deleteCashbackGamesItem.pending, (state, action) => {
      state.processingItemId = +action.meta.arg || undefined;
    });
    builder.addCase(deleteCashbackGamesItem.fulfilled, (state) => {
      state.isListFetched = false;
      state.processingItemId = undefined;
    });
    builder.addCase(deleteCashbackGamesItem.rejected, (state) => {
      state.isListFetched = false;
      state.processingItemId = undefined;
    });
  },
});

export const {
  handlePaginate,
  handleSelectItem,
  handleSelectAll,
  handleClear,
  handleClearFields,
  handleUpdateFields,
  handlePaginateGames,
  handleSelectAllGames,
  handleSelectItemGames,
} = cashbackGamesSlice.actions;
